import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  CreditCard,
  Database,
  Mail,
  Phone,
  RefreshCw,
  Settings2,
} from "lucide-react";
import type {
  EnrichmentJob,
  EnrichmentQueueLead,
  EnrichmentQueueType,
  EnrichmentSchedule,
} from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { useDebouncedValue } from "@/hooks/usePagination";
import {
  getDataHealth,
  getEnrichmentQueue,
  listEnrichmentJobs,
  putEnrichmentSchedule,
  runEnrichmentEnrich,
  runEnrichmentScheduleNow,
} from "@/api/enrichment";

type QueueTab = "missing_email" | "needs_refresh";

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function jobKindLabel(kind: EnrichmentJob["kind"]): string {
  switch (kind) {
    case "manual_email":
      return "Enrich email";
    case "manual_phone":
      return "Enrich phone";
    case "bulk_missing_email":
      return "Missing emails";
    case "bulk_missing_phone":
      return "Missing phones";
    case "scheduled_missing_email":
      return "Scheduled emails";
    case "needs_refresh":
      return "Needs refresh";
    default:
      return kind;
  }
}

function StatCard({
  label,
  value,
  hint,
  action,
}: {
  label: string;
  value: string | number;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-foreground">
        {value}
      </p>
      {hint ? (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

function QueueTable({
  contacts,
  selected,
  onToggle,
  onToggleAll,
}: {
  contacts: EnrichmentQueueLead[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (ids: string[], on: boolean) => void;
}) {
  const allIds = contacts.map((c) => c.leadId);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selected.has(id));

  return (
    <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => onToggleAll(allIds, e.target.checked)}
                aria-label="Select all"
              />
            </th>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Company</th>
            <th className="px-3 py-2 font-medium">Email</th>
            <th className="px-3 py-2 font-medium">Phone</th>
            <th className="px-3 py-2 font-medium">Last enriched</th>
          </tr>
        </thead>
        <tbody>
          {contacts.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="px-3 py-8 text-center text-muted-foreground"
              >
                No contacts in this queue.
              </td>
            </tr>
          ) : (
            contacts.map((c) => (
              <tr
                key={c.leadId}
                className="border-b border-border/70 last:border-0"
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(c.leadId)}
                    onChange={() => onToggle(c.leadId)}
                    aria-label={`Select ${c.name}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <Link
                    to={`/user/leads?q=${encodeURIComponent(c.name)}`}
                    className="font-medium text-foreground hover:text-brand"
                  >
                    {c.name}
                  </Link>
                  {c.title ? (
                    <p className="text-xs text-muted-foreground">{c.title}</p>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <Link
                    to={`/user/accounts/${encodeURIComponent(c.accountId)}`}
                    className="text-muted-foreground hover:text-brand"
                  >
                    {c.accountId}
                  </Link>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {c.email || "—"}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {c.phone || "—"}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {formatWhen(c.lastEnrichedAt)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function DataHealthPage() {
  const qc = useQueryClient();
  const [queueTab, setQueueTab] = useState<QueueTab>("missing_email");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);

  const healthQuery = useQuery({
    queryKey: ["enrichment", "health"],
    queryFn: () => getDataHealth(),
    staleTime: 15_000,
  });

  const jobsQuery = useQuery({
    queryKey: ["enrichment", "jobs"],
    queryFn: () => listEnrichmentJobs(40),
    staleTime: 15_000,
  });

  const queueType: EnrichmentQueueType = queueTab;
  const queueQuery = useQuery({
    queryKey: ["enrichment", "queue", queueType, debouncedQ],
    queryFn: () =>
      getEnrichmentQueue(queueType, {
        limit: 50,
        offset: 0,
        q: debouncedQ || undefined,
      }),
    staleTime: 10_000,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["enrichment"] });
    qc.invalidateQueries({ queryKey: ["people", "usage"] });
  };

  const enrichMutation = useMutation({
    mutationFn: runEnrichmentEnrich,
    onSuccess: (res) => {
      setMessage(
        `Enriched ${res.job.enrichedCount} of ${res.job.requestedCount} contacts.`,
      );
      setError(null);
      setSelected(new Set());
      invalidateAll();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Enrich failed");
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: (schedule: EnrichmentSchedule) => putEnrichmentSchedule(schedule),
    onSuccess: () => {
      setMessage("Schedule saved.");
      setScheduleOpen(false);
      invalidateAll();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Schedule save failed");
    },
  });

  const runNowMutation = useMutation({
    mutationFn: () => runEnrichmentScheduleNow(),
    onSuccess: (res) => {
      setMessage(
        res.job.requestedCount === 0
          ? "Queue was empty — nothing to enrich."
          : `Run finished: ${res.job.enrichedCount} enriched.`,
      );
      invalidateAll();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Run now failed");
    },
  });

  const health = healthQuery.data;
  const contacts = queueQuery.data?.contacts ?? [];
  const jobs = jobsQuery.data?.jobs ?? [];

  const scheduleDraft = useMemo<EnrichmentSchedule>(
    () =>
      health?.schedule ?? {
        enabled: false,
        kind: "missing_email",
        cadenceDays: 7,
      },
    [health?.schedule],
  );
  const [localSchedule, setLocalSchedule] = useState<EnrichmentSchedule | null>(
    null,
  );
  const scheduleForm = localSchedule ?? scheduleDraft;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (ids: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const enrichSelected = (opts: {
    revealEmail: boolean;
    revealPhone: boolean;
    kind:
      | "bulk_missing_email"
      | "bulk_missing_phone"
      | "needs_refresh"
      | "manual_email";
  }) => {
    const ids = [...selected];
    if (ids.length === 0) {
      setError("Select at least one contact.");
      return;
    }
    enrichMutation.mutate({
      leadIds: ids,
      revealEmail: opts.revealEmail,
      revealPhone: opts.revealPhone,
      kind: opts.kind,
    });
  };

  const creditLabel = health?.credits.connected
    ? [
        health.credits.emailCredits != null
          ? `${health.credits.emailCredits} email`
          : null,
        health.credits.mobileCredits != null
          ? `${health.credits.mobileCredits} mobile`
          : null,
        health.credits.exportCredits != null
          ? `${health.credits.exportCredits} export`
          : null,
      ]
        .filter(Boolean)
        .join(" · ") || health.credits.label || "Connected"
    : "Not connected";

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-brand" aria-hidden />
            <h1 className="text-2xl font-bold text-foreground">
              Data Health Center
            </h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Last {health?.periodDays ?? 30} days · Contact coverage, credits, and
            enrichment jobs
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => invalidateAll()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setLocalSchedule(scheduleDraft);
              setScheduleOpen(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40"
          >
            Automate enrichment
          </button>
          <button
            type="button"
            disabled={runNowMutation.isPending}
            onClick={() => runNowMutation.mutate()}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
          >
            Run now
          </button>
        </div>
      </header>

      {message ? (
        <p className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
          {message}
        </p>
      ) : null}
      {error ? (
        <p className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      ) : null}

      {healthQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading health…</p>
      ) : healthQuery.error ? (
        <p className="text-sm text-red-600">
          {healthQuery.error instanceof Error
            ? healthQuery.error.message
            : "Failed to load"}
        </p>
      ) : health ? (
        <>
          <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Contacts"
              value={health.contacts.total}
              hint={`${health.contacts.withEmail} with email · ${health.contacts.withPhone} with phone`}
            />
            <StatCard
              label="Credits"
              value={creditLabel}
              hint={
                health.credits.connected
                  ? "Apollo usage"
                  : "Connect Apollo in Settings → Integrations"
              }
              action={
                !health.credits.connected ? (
                  <Link
                    to="/settings/integrations"
                    className="text-xs text-brand hover:underline"
                  >
                    Connect Apollo
                  </Link>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <CreditCard className="h-3 w-3" /> Usage
                  </span>
                )
              }
            />
            <StatCard
              label="CRM status"
              value={
                health.crm.hubspot || health.crm.salesforce
                  ? "Connected"
                  : "Not connected"
              }
              hint={[
                health.crm.hubspot ? "HubSpot" : null,
                health.crm.salesforce ? "Salesforce" : null,
              ]
                .filter(Boolean)
                .join(" · ") || "Link CRM in Integrations"}
            />
            <StatCard
              label="Needs refresh"
              value={health.needsRefresh}
              hint={`Not enriched in ${health.refreshDays} days`}
            />
          </section>

          <section className="mb-6 grid gap-3 sm:grid-cols-2">
            <StatCard
              label="Missing emails"
              value={health.missingEmails}
              action={
                <button
                  type="button"
                  disabled={enrichMutation.isPending || health.missingEmails === 0}
                  onClick={() => {
                    setQueueTab("missing_email");
                    void getEnrichmentQueue("missing_email", {
                      limit: 50,
                    }).then((q) => {
                      enrichMutation.mutate({
                        leadIds: q.contacts.map((c) => c.leadId),
                        revealEmail: true,
                        revealPhone: false,
                        kind: "bulk_missing_email",
                      });
                    });
                  }}
                  className="rounded-md bg-brand px-2.5 py-1 text-xs font-medium text-brand-foreground disabled:opacity-50"
                >
                  <span className="inline-flex items-center gap-1">
                    <Mail className="h-3 w-3" /> Enrich missing emails
                  </span>
                </button>
              }
            />
            <StatCard
              label="Missing phones"
              value={health.missingPhones}
              action={
                <button
                  type="button"
                  disabled={
                    enrichMutation.isPending || health.missingPhones === 0
                  }
                  onClick={() => {
                    void getEnrichmentQueue("missing_phone", {
                      limit: 50,
                    }).then((q) => {
                      enrichMutation.mutate({
                        leadIds: q.contacts.map((c) => c.leadId),
                        revealEmail: false,
                        revealPhone: true,
                        kind: "bulk_missing_phone",
                      });
                    });
                  }}
                  className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted/40 disabled:opacity-50"
                >
                  <span className="inline-flex items-center gap-1">
                    <Phone className="h-3 w-3" /> Enrich missing phones
                  </span>
                </button>
              }
            />
          </section>

          <section className="mb-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border bg-card p-4 sm:col-span-2">
              <h2 className="text-sm font-semibold text-foreground">
                Enrichment coverage
              </h2>
              <div className="mt-3 grid grid-cols-4 gap-2 text-center text-sm">
                {(
                  [
                    ["None", health.contacts.enrichment.none],
                    ["Partial", health.contacts.enrichment.partial],
                    ["Enriched", health.contacts.enrichment.enriched],
                    ["Failed", health.contacts.enrichment.failed],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="rounded-md bg-muted/30 px-2 py-3">
                    <p className="text-lg font-semibold tabular-nums">{value}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="text-sm font-semibold text-foreground">
                Jobs (30 days)
              </h2>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Total</dt>
                  <dd className="font-medium">{health.jobsLast30Days.total}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Succeeded</dt>
                  <dd className="font-medium">
                    {health.jobsLast30Days.succeeded}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Failed</dt>
                  <dd className="font-medium">{health.jobsLast30Days.failed}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Records enriched</dt>
                  <dd className="font-medium">
                    {health.jobsLast30Days.recordsEnriched}
                  </dd>
                </div>
              </dl>
            </div>
          </section>

          {health.topCompanies.length > 0 ? (
            <section className="mb-6">
              <h2 className="mb-2 text-sm font-semibold text-foreground">
                Top companies by gaps
              </h2>
              <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border bg-muted/30 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Company</th>
                      <th className="px-3 py-2">Missing email</th>
                      <th className="px-3 py-2">Missing phone</th>
                      <th className="px-3 py-2">Needs refresh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.topCompanies.map((b) => (
                      <tr key={b.accountId} className="border-b border-border/70">
                        <td className="px-3 py-2">
                          <Link
                            to={`/user/accounts/${encodeURIComponent(b.accountId)}`}
                            className="inline-flex items-center gap-1.5 hover:text-brand"
                          >
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                            {b.accountId}
                          </Link>
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {b.missingEmails}
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {b.missingPhones}
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {b.needsRefresh}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <section className="mb-6">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">
                Enrichment job activity
              </h2>
            </div>
            <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="border-b border-border bg-muted/30 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Kind</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Requested</th>
                    <th className="px-3 py-2">Enriched</th>
                    <th className="px-3 py-2">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-8 text-center text-muted-foreground"
                      >
                        No enrichment jobs yet. Enrich from People or the
                        queues below.
                      </td>
                    </tr>
                  ) : (
                    jobs.map((j) => (
                      <tr key={j.jobId} className="border-b border-border/70">
                        <td className="px-3 py-2 text-muted-foreground">
                          {formatWhen(j.createdAt)}
                        </td>
                        <td className="px-3 py-2">{jobKindLabel(j.kind)}</td>
                        <td className="px-3 py-2 capitalize">{j.status}</td>
                        <td className="px-3 py-2 tabular-nums">
                          {j.requestedCount}
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {j.enrichedCount}
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {j.failedCount}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-6">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-foreground">
                  Enrichable records
                </h2>
                <p className="text-xs text-muted-foreground">
                  Select contacts, then enrich. Or use the widget actions above.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={enrichMutation.isPending || selected.size === 0}
                  onClick={() =>
                    enrichSelected({
                      revealEmail: true,
                      revealPhone: false,
                      kind:
                        queueTab === "needs_refresh"
                          ? "needs_refresh"
                          : "bulk_missing_email",
                    })
                  }
                  className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
                >
                  Enrich selected emails
                </button>
                <button
                  type="button"
                  disabled={enrichMutation.isPending || selected.size === 0}
                  onClick={() =>
                    enrichSelected({
                      revealEmail: false,
                      revealPhone: true,
                      kind: "bulk_missing_phone",
                    })
                  }
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                >
                  Enrich selected phones
                </button>
              </div>
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="flex gap-1 rounded-md border border-border p-0.5">
              {(
                [
                  ["missing_email", "Missing values"],
                  ["needs_refresh", "Needs refresh"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setQueueTab(id);
                    setSelected(new Set());
                  }}
                  className={cn(
                    "rounded px-3 py-1.5 text-xs font-medium transition",
                    queueTab === id
                      ? "bg-brand text-brand-foreground"
                      : "text-muted-foreground hover:bg-muted/40",
                  )}
                >
                  {label}
                  {id === "missing_email" && health
                    ? ` (${health.missingEmails})`
                    : null}
                  {id === "needs_refresh" && health
                    ? ` (${health.needsRefresh})`
                    : null}
                </button>
              ))}
              </div>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search queue…"
                className="ml-auto w-48 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
              />
            </div>
            {queueQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading queue…</p>
            ) : (
              <QueueTable
                contacts={contacts}
                selected={selected}
                onToggle={toggle}
                onToggleAll={toggleAll}
              />
            )}
          </section>

          <section className="rounded-lg border border-border bg-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-foreground">
                  Data waterfall
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Email waterfall:{" "}
                  {health.waterfall.waterfallEmail ? "on" : "off"} · Phone
                  waterfall:{" "}
                  {health.waterfall.waterfallPhone ? "on" : "off"} · Providers:{" "}
                  {health.waterfall.providerOrder.join(" → ")}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Schedule:{" "}
                  {health.schedule.enabled
                    ? `${health.schedule.kind.replace("_", " ")} every ${health.schedule.cadenceDays}d`
                    : "off"}
                  {health.schedule.nextRunAt
                    ? ` · next ${formatWhen(health.schedule.nextRunAt)}`
                    : ""}
                </p>
              </div>
              <Link
                to="/settings/org"
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
              >
                <Settings2 className="h-3.5 w-3.5" />
                Org config
              </Link>
            </div>
          </section>
        </>
      ) : null}

      {scheduleOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-lg">
            <h3 className="text-lg font-semibold">Automate enrichment</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              v1 stores cadence and supports Run now. A background cron can call
              the same endpoint later.
            </p>
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scheduleForm.enabled}
                onChange={(e) =>
                  setLocalSchedule({
                    ...scheduleForm,
                    enabled: e.target.checked,
                  })
                }
              />
              Enable schedule
            </label>
            <label className="mt-3 block text-sm">
              Kind
              <select
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={scheduleForm.kind}
                onChange={(e) =>
                  setLocalSchedule({
                    ...scheduleForm,
                    kind: e.target.value as EnrichmentSchedule["kind"],
                  })
                }
              >
                <option value="missing_email">Missing emails</option>
                <option value="needs_refresh">Needs refresh</option>
              </select>
            </label>
            <label className="mt-3 block text-sm">
              Cadence (days)
              <input
                type="number"
                min={1}
                max={365}
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={scheduleForm.cadenceDays}
                onChange={(e) =>
                  setLocalSchedule({
                    ...scheduleForm,
                    cadenceDays: Number(e.target.value) || 7,
                  })
                }
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setScheduleOpen(false)}
                className="rounded-md border border-border px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={scheduleMutation.isPending}
                onClick={() => scheduleMutation.mutate(scheduleForm)}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
